import * as Sentry from '@sentry/react';
import {ImageMetadata} from '@wandb/weave/common/types/media';
import * as React from 'react';
import {CSSProperties, useEffect, useMemo, useRef, useState} from 'react';

import {
  labelComponent,
  makeCaptions,
  useNaturalDimensions,
} from '../../util/media';
import {getImageMedia, MediaCardProps, TileMedia} from '../MediaCard';
import MessageMediaNotFound from '../MessageMediaNotFound';
import {SingleRunLink} from '../MultiRunWorkspace/SingleRunMode';
import {useGetTrackingCallbacks} from '../PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {useMediaBrowserModalContext} from '../PanelMediaBrowser/modal/MediaBrowserModalContext';
import {InstrumentedCenteredWaveLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {BoundingBoxes} from './BoundingBoxes';
import Image from './Image';
import {MasksOverlay} from './MasksOverlay';
import {getBBoxData, getImageInfo, getMaskData} from './utils';

type ImageCardProps = MediaCardProps;

const ImageCardComp = (props: ImageCardProps) => {
  const {width, height, mediaIndex, mediaKey, globalStep, tileMedia, loading} =
    props;
  if (tileMedia == null) {
    return (
      <div data-test="image-card-wrapper" style={{height: '100%'}}>
        <div
          data-test="image-card-comp"
          className="image-card"
          style={{width, height, display: 'flex'}}>
          {loading ? (
            <Loader name="panel-content-loader" size="small" />
          ) : (
            <MessageMediaNotFound
              basic
              mediaIndex={mediaIndex}
              mediaKey={mediaKey}
              stepIndex={globalStep}
              mediaType="images"
            />
          )}
        </div>
      </div>
    );
  }

  return <ImageCardInner {...props} tileMedia={tileMedia} loading={loading} />;
};
export const ImageCard = React.memo(ImageCardComp);
export default ImageCard;

type ImageCardInnerProps = ImageCardProps & {
  tileMedia: NonNullable<TileMedia>;
  loading?: boolean;
};

export const ImageCardInner: React.FunctionComponent<ImageCardInnerProps> =
  React.memo(props => {
    const {
      run,
      customRunName,
      globalStep,
      width,
      height,
      mediaKey,
      mediaIndex,
      mediaWidth,
      mediaHeight,
      runSignature,
      maskOptions,
      controls,
      mediaPanelRef,
      actualSize,
      tileMedia,
      loading,
      getMediaTracking,
      tileMediaSpec,
      omitRunLabel,
      isWithinMediaBrowserModal,
      omitCaptions,
    } = props;
    const {step, historyRow, objectURL, internalURL, filePath, thumbnails} =
      tileMedia;

    const numLabels = props.labels?.length ?? 0;
    const includeRunLabel = !omitRunLabel && numLabels > 0;

    const {trackSuccess, trackError} = useGetTrackingCallbacks(
      tileMedia,
      tileMediaSpec,
      getMediaTracking
    );

    let imageURL = objectURL;
    if (internalURL) {
      try {
        // Encode the URL and fix any images with a # in the name as `#` is a valid URI component but not
        // for images being rendered as they will not link to any portion of the page.
        const url = new URL(encodeURI(internalURL).replace(/#/g, '%23'));
        /**
         * Notes on image sizing:
         * We request a larger dimensioned image what the panel technically needs for safety. Why is this "more safe"?
         * 1. We won't request new images if the panel is resized, therefore it'll be nice to have an image that's big enough to handle some expansion without losing resolution
         * 2. Is there still some display constraint around needing higher pixel images for devices with high-pixel density displays? I vaguely remember this from HTML of yesteryear
         * 3. Because the primary value in doing this at all is throttling down YUUUUUGE images o(think medical images of 3500x3500 at 150mb), the performance cost of being a little greedy on asking for extra dimensions is low.
         * - A 200x200 image contains 4000 pixel
         * - A 400x400 image contains 16000 pixels (2x as big, 4x more pixels)
         * So while this seems like we might be asking for a lot of extra pixels we don't need, the value in this is going from 3500 -> 400, and the cost of getting 400 instead of 200 is negligible.
         */

        // Use a more conservative sizing approach to improve caching
        // Request a reasonable size that works for most use cases
        const targetHeight = Math.min(Math.round(height * 1.5), 800);
        url.searchParams.set('height', targetHeight.toString());
        url.searchParams.delete('width');
        imageURL = url.toString();
      } catch (err) {
        // if anything goes wrong here we'll log it but we've got the blob URL as fallback
        Sentry.captureException(err);
      }
    }

    const {openModal} = useMediaBrowserModalContext();

    const masksRef = useRef<HTMLDivElement | null>(null);
    const boxesRef = useRef<HTMLDivElement | null>(null);

    const [naturalDimensions, imgRef] = useNaturalDimensions();
    const [actualImageSize, setActualImageSize] = useState<{
      width: number;
      height: number;
    } | null>({width, height});

    // Measure the actual rendered size of the image element (not natural dimensions)
    // This accounts for CSS object-fit: contain and container constraints
    useEffect(() => {
      const imgEl = imgRef.current;
      if (!imgEl) {
        return;
      }

      const updateSize = () => {
        const rect = imgEl.getBoundingClientRect();
        // assume the default/current size if the image is not visible/loaded
        if (rect.width && rect.height) {
          setActualImageSize({
            width: rect.width,
            height: rect.height,
          });
        }
      };

      // Initial measurement
      updateSize();

      // Set up ResizeObserver to update when image size changes
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(imgEl);

      // Also listen for image load events in case dimensions change
      imgEl.addEventListener('load', updateSize);

      return () => {
        resizeObserver.disconnect();
        imgEl.removeEventListener('load', updateSize);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [naturalDimensions]); // Re-run when image loads (naturalDimensions becomes available)

    const rolledBack = globalStep !== step;

    const mediaMetadata = getImageMedia(
      {
        historyRow,
        mediaKey,
      },
      mediaIndex
    ) as ImageMetadata | null;

    const paths = getImageInfo({
      imgMedia: mediaMetadata,
      step,
      mediaIndex,
      mediaKey,
      runSignature,
    });
    const isSprite = paths.type === 'sprite';

    // Compute pixel offset for sprite based images
    //
    // This will be scaled to the card and used for display
    // to calculate to offset.
    const spritePixelOffset = isSprite ? mediaIndex * width : 0;

    // Compute the pixel offset for the original sprite
    // This is used for downloading
    const originalWidth = mediaMetadata?.width ?? null;
    const originalPixelOffset =
      isSprite && originalWidth != null ? mediaIndex * originalWidth : 0;

    const getWBFileDataParams = useMemo(
      () => ({
        currentMediaMetadata: mediaMetadata,
        mediaIndex,
        mediaKey,
        run,
      }),
      [mediaMetadata, mediaIndex, mediaKey, run]
    );
    const maskData = useMemo(
      () => getMaskData(getWBFileDataParams),
      [getWBFileDataParams]
    );
    const boundingBoxData = useMemo(
      () => getBBoxData(getWBFileDataParams),
      [getWBFileDataParams]
    );

    const mediaSize = useMemo(() => {
      if (!isSprite && naturalDimensions != null) {
        // HAX: Turns out the media metadata in the run history only stores one media size per step,
        // so we don't even have data for how large the non-first image is when a user logs multiple media under one key in a single step.
        // So to hack around this limitation, we wait until the image is rendered to get its natural dimensions and use that instead.
        return naturalDimensions;
      }
      if (mediaWidth && mediaHeight) {
        return {width: mediaWidth, height: mediaHeight};
      }
      return null;
    }, [isSprite, naturalDimensions, mediaWidth, mediaHeight]);

    const showImage = !maskOptions || !!maskOptions?.showImage;

    const imgStyle: CSSProperties = {
      left: -(actualSize ? originalPixelOffset : spritePixelOffset),
      height: '100%',
      display: showImage ? 'initial' : 'none',
      maxWidth: 'none',
      ...(!isSprite ? {width: '100%', objectFit: 'contain'} : {}),
    };

    const title = customRunName ?? run.displayName ?? runSignature.runName;
    const titleLink = <SingleRunLink {...runSignature} linkText={title} />;

    // Get captions if not omitted (captions are rendered in TileFooterSlot via CaptionSlot in new layout)
    // Default omitCaptions to true to prevent double-rendering when using new layout system
    const captions = omitCaptions
      ? []
      : makeCaptions(mediaMetadata, mediaIndex);
    // Need to add 16px to the height to account for the label so the image doesn't shrink when the label is visible
    // const heightWithLabel = includeRunLabel ? height + 16 * numLabels : height;

    // Use the actual rendered image size for masks/boxes, falling back to card size
    // This ensures masks align with the actual image dimensions, not the card container
    const cardSize = useMemo(() => {
      return actualImageSize ?? {width, height};
    }, [actualImageSize, width, height]);

    const cardJSX = (
      <div
        className="ui card image-card content-card"
        data-test="image-content-card"
        data-mask-options={maskOptions?.maskKeys.join(',')}
        style={{width, height}}>
        <div className="image-card-image hover:opacity-[0.88] w-full">
          <Image
            width={width}
            step={step}
            imgRef={imgRef}
            imageURL={imageURL}
            imgStyle={imgStyle}
            trackSuccess={trackSuccess}
            trackError={trackError}
            rolledBack={rolledBack}
            globalStep={globalStep}
            filePath={filePath}
            originalWidth={originalWidth}
            originalPixelOffset={originalPixelOffset}
            runSignature={runSignature}
            thumbnails={
              // We should always show the original image if the user has selected "Use original size" or "Fit to available space",
              props.config.actualSize === true ||
              props.config.fitToDimension === true ||
              isWithinMediaBrowserModal // if the user has clicked the image to open the modal, then they want to look at it at full size
                ? undefined
                : thumbnails
            }
          />

          {mediaSize && maskData.length > 0 && (
            <MasksOverlay
              masksRef={masksRef}
              mediaKey={mediaKey}
              maskData={maskData}
              maskOptions={maskOptions}
              controls={controls?.segmentationMaskControl}
              cardSize={cardSize}
              mediaSize={mediaSize}
              runSignature={runSignature}
              mediaPanelRef={mediaPanelRef}
            />
          )}
          {mediaSize && boundingBoxData.length > 0 && (
            <div ref={boxesRef} className="bounding-boxes__container">
              {boundingBoxData.map(({key, wbFile, classLabels}) => (
                <BoundingBoxes
                  style={{position: 'absolute', top: 0, left: 0}}
                  key={key}
                  mediaKey={mediaKey}
                  boxStyle={
                    controls?.boundingBoxControl?.styles?.[mediaKey]?.[key]
                  }
                  boxToggles={
                    controls?.boundingBoxControl?.toggles?.[mediaKey]?.[key]
                  }
                  boxSliders={controls?.boundingBoxControl?.sliders}
                  boxKey={key}
                  classLabels={classLabels}
                  runSignature={runSignature}
                  boxFileInfo={wbFile}
                  cardSize={cardSize}
                  mediaSize={mediaSize}
                  mediaPanelRef={mediaPanelRef}
                />
              ))}
            </div>
          )}
        </div>
        {/* CAPTIONS - Only render if not omitted (new layout renders in TileFooterSlot) */}
        {captions.length > 0 && (
          <div className="image-card-caption break-words">{captions}</div>
        )}
      </div>
    );

    const enableFullscreen = showImage;
    if (!enableFullscreen) {
      return cardJSX;
    }

    // Handle image click to open modal
    const handleImageClick = () => {
      if (!tileMediaSpec) {
        return;
      }
      openModal({
        mediaPanelRef,
        tileMediaSpec: tileMediaSpec,
        labels: props.labels || [],
        imageURL,
        originalPixelOffset,
        runSignature,
        fullScreenTitle: title,
        imageDimensions: mediaSize,
        boundingBoxData,
        mediaKey,
        mediaSize: mediaSize ?? {width: 0, height: 0},
        controls: controls ?? {},
      });
    };
    return (
      <div onClick={handleImageClick}>
        {includeRunLabel && (
          <div className="flex-0 h-16 w-full">
            {labelComponent(props, step, titleLink)}
          </div>
        )}
        {cardJSX}
      </div>
    );
  });
